import { selectLanguage } from 'app/modules/auth'
import { FLOW_TYPE, Languages, clientTimeZoneOffset, DEFAULT_WORKER } from 'app/utils/common/constants'
import { SpServiceAvailable } from 'app/utils/common/models'
import { KTSVG } from 'app/utils/helpers'
import { pickRouteOptimizationCfg } from 'app/utils/helpers/OrderDrawerHelpers'
import { FieldRadioGroup, TextInput } from 'app/utils/helpers/components/Form'
import { FormikErrors, FormikHelpers } from 'formik'
import moment from 'moment'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from 'setup/redux/Hooks'
import { Worker, PackageServiceResponse } from '../../models/OrderDrawerModel'
import { ticketApi } from '../../redux/TicketApi'
import { computeCalendarLimitDays, loadPackageCalendarV2 } from '../../redux/PackageAvailabilityV2'
import AccordionItem from './AccordionItem'
import { IForm } from './type'
import { Response } from 'setup/axios/HttpResponse'
import { toast } from 'react-toastify'
import { ToastDefaultConfig } from 'setup/toast/ToastConfig'

const WorkerComponent = (props: {
    values: IForm,
    errors: FormikErrors<IForm>,
    setFieldValue: FormikHelpers<IForm>['setFieldValue']
    setFieldTouched: FormikHelpers<IForm>['setFieldTouched'],
    setValues: FormikHelpers<IForm>['setValues']
    rtl?: boolean;
    language: string,
    index?: number;
}) => {
    const { index = 0 } = props
    const user = useAppSelector(s => s.auth.user)
    const isClusterLogic = useAppSelector(s => s.auth.user?.isWatanCompany)
    const { t } = useTranslation()
    const ref = useRef({
        timeout: 0
    })
    const workerCalendarRequestRef = useRef(0)

    const { values, rtl = false, setValues, setFieldValue, setFieldTouched, language } = props;
    const { flow_type, worker_available, number_of_workers, preSelectSP } = values

    const isOffer = values.service_selected?.[0]?.type == 'offer'

    const [state, setState] = useState<{
        textSearch: string
        workersListLoading: boolean
    }>({ textSearch: '', workersListLoading: false })

    const trans = useMemo(() => {
        return flow_type === FLOW_TYPE.PACKAGE ? {
            title: `${index}. ${t('CREATE_ORDER_DRAWER.SECTION_4.TITLE')}`,
            section_title: t('CREATE_ORDER_DRAWER.SECTION_4.SUB_TITLE'),
            search_placeholder: t('CREATE_ORDER_DRAWER.SECTION_4.SEARCH_WORKER_PLACEHOLDER')
        }
            : {
                title: `${index}. ${t('CREATE_ORDER_DRAWER.SECTION_4.TITLE')}`,
                section_title: t('CREATE_ORDER_DRAWER.SECTION_4.SUB_TITLE'),
                search_placeholder: t('CREATE_ORDER_DRAWER.SECTION_4.SEARCH_PROVIDER_PLACEHOLDER')
            }
    }, [flow_type, language])

    useEffect(() => {
        setState(prevState => {
            return {
                ...prevState,
                textSearch: ''
            }
        })
    }, [worker_available])

    // Keep spinner until package worker list is ready (handles stale fetch clearing loadingType early).
    useEffect(() => {
        if (flow_type === FLOW_TYPE.PACKAGE) {
            if (!values.package || values.isOutOfCapacity) {
                setState(prev => ({ ...prev, workersListLoading: false }))
                return
            }
            if (values.preSelectSP) {
                setState(prev => ({ ...prev, workersListLoading: false }))
                return
            }
            setState(prev => ({ ...prev, workersListLoading: true }))
            return
        }

        setState(prev => ({
            ...prev,
            workersListLoading: values.loadingType === 'worker',
        }))
    }, [
        flow_type,
        values.loadingType,
        values.package,
        values.isOutOfCapacity,
        values.preSelectSP,
    ])


    const displayedWorkers = useMemo(() => {
        if (flow_type === FLOW_TYPE.PACKAGE) {
            if (values.isOutOfCapacity) {
                return []
            }
            if (isClusterLogic && worker_available?.length === 0) {
                return []
            }
            if (worker_available && number_of_workers) {
                const defaultWorkers = [
                    {
                        workerId: '0',
                        workerNameEn: t('CREATE_ORDER_DRAWER.BASE_ON_AVAILABILITY'),
                        workerNameAr: t('CREATE_ORDER_DRAWER.BASE_ON_AVAILABILITY'),
                        startDate: preSelectSP?.dayAvailable?.find(day => day.isAvailable)?.dateCheck,
                        isAuto: true,
                        personalImageUrl: '/assets/images/common/unknown-worker.png',
                    },
                ] as Worker[]
                const displayed_workers = number_of_workers > 1 ? defaultWorkers : [...defaultWorkers, ...worker_available]
                return displayed_workers.filter((worker) => 'workerId' in worker && worker.startDate)
            }
            return []
        }
        else {
            return worker_available
        }
    }, [number_of_workers, flow_type, worker_available, values.isOutOfCapacity, preSelectSP])

    useEffect(() => {
        if (flow_type !== FLOW_TYPE.PACKAGE) {
            const spIds = values.date_time && 'time' in values.date_time && values.date_time?.time ? values.date_time?.time?.spIds : [];
            const subIds = isOffer ? [] : values.service_selected?.map(e => e.parentId) || [];
            const subSubIds = isOffer ? [] : values.service_selected?.map(e => e.id) || [];
            const timeout = new Date().getTime();
            ref.current = { ...ref.current, timeout: timeout }
            fetchInfo(timeout, isOffer, subIds, subSubIds, spIds)
        }
        return () => {
            ref.current = { ...ref.current, timeout: new Date().getTime() }
        }
    }, [flow_type, values.date_time && 'time' in values.date_time ? values.date_time?.time : undefined])

    // Function
    const fetchInfo = async (timeout: number, isOffer: boolean, subIds: number[], subSubIds: number[], spIds: string[]) => {
        setValues(values => ({ ...values, loadingType: 'worker' }))
        const spTeams = !isOffer ? [] : (await ticketApi.getAllSPTeam()).data
        const worker_available = isOffer ?
            spIds.map(e => {
                const sp = spTeams.filter(e1 => e1.id == e)[0]
                return {
                    spId: e,
                    fullNameAr: sp?.fullNameAr,
                    fullNameEn: sp?.fullNameEn,
                    isOffer: true,
                    personalImageUrl: sp?.personalImageUrl
                } as SpServiceAvailable
            })
            : spIds.length == 0 || subSubIds.length <= 0 ? []
                : (await ticketApi.getSpAvailable({
                    spIds: spIds,
                    subSubIdsSelected: subSubIds
                })).data.map(e => {
                    let servicesAvailable = e.subSubServiceScopes.filter((x) => x.isAvailable);
                    let servicesUnavailable = e.subSubServiceScopes.filter((x) => !x.isAvailable);
                    e.isAvailableAll = servicesAvailable.length === (values.service_selected?.length || 0);

                    if (rtl) {
                        e.servicesAvailable = servicesAvailable.map((x) => x.subSubNameAr).join(', ');
                        e.servicesUnavailable = servicesUnavailable.map((x) => x.subSubNameAr).join(', ');
                    } else {
                        e.servicesAvailable = servicesAvailable.map((x) => x.subSubNameEn).join(', ');
                        e.servicesUnavailable = servicesUnavailable.map((x) => x.subSubNameEn).join(', ');
                    }
                    return e;
                })
        if (timeout !== ref.current.timeout) return;
        setValues(values => ({ ...values, worker_available, worker: undefined, loadingType: undefined }))
    }

    const handleFavoriteWorker = (worker: SpServiceAvailable | Worker) => {
        if ('workerId' in worker) {
            const index = values.favWorkerIds.indexOf(worker.workerId)
            const new_favWorkerIds = index === -1 ? [...values.favWorkerIds, worker.workerId] : [...values.favWorkerIds.slice(0, index), ...values.favWorkerIds.slice(index + 1)]

            // Handle Deselected Tracking
            let new_deselectedFavWorkerIds = values.deselectedFavWorkerIds || [];
            if (index !== -1) {
                // Determine if it was deselected (was present, now removing)
                if (!new_deselectedFavWorkerIds.includes(worker.workerId)) {
                    new_deselectedFavWorkerIds = [...new_deselectedFavWorkerIds, worker.workerId];
                }
            } else {
                // Determine if it was re-selected (was absent, now adding)
                if (new_deselectedFavWorkerIds.includes(worker.workerId)) {
                    new_deselectedFavWorkerIds = new_deselectedFavWorkerIds.filter(id => id !== worker.workerId);
                }
            }

            setFieldValue('favWorkerIds', new_favWorkerIds)
            setFieldValue('deselectedFavWorkerIds', new_deselectedFavWorkerIds)
        }
    }

    // For Watan company: Call API to get updated dayAvailable when worker changes
    useEffect(() => {
        if (
            isClusterLogic &&
            flow_type === FLOW_TYPE.PACKAGE &&
            values.worker &&
            values.worker !== DEFAULT_WORKER &&
            values.main_service &&
            values.preSelectSP &&
            values.package
        ) {
            const requestedWorkerId = values.worker
            const requestId = ++workerCalendarRequestRef.current

            // Sync — disable calendar before async fetch starts (avoid stale availability flash).
            setValues(values => ({
                ...values,
                loadingType: 'worker',
                workerDayAvailable: undefined,
                workerDayAvailableWorkerId: undefined,
                selectedWorkerRouteOptimizationCfg: undefined,
                selectedWorkerVisitReminder: undefined,
                selectedDateRouteOptimizationCfg: undefined,
                selectedDateVisitReminder: undefined,
            }))

            const fetchPackageAvailable = async () => {
                try {
                    if (!values.main_service || !values.preSelectSP) return

                    // V2: when the worker changes, only the calendar needs to be refetched.
                    // Details (package info, full worker list, price) stay the same and are reused from the cache.
                    const { legacy: data, calendar } = await loadPackageCalendarV2(
                        {
                            packageWorkflowId: values.main_service.data?.packageWorkFlowId || 0,
                            countryId: values.location?.countryId || 0,
                            packageId: Number(values.package),
                            majorId: values.main_service.id,
                            clientTimeOffset: -clientTimeZoneOffset,
                            companyId: values.preSelectSP.companyId || '',
                            customerId: values.customer_id,
                            customerPhone: values.phone,
                            customerLat: values.location?.lat,
                            customerLng: values.location?.lng,
                            spIds: values.spIds,
                            zoneIds: values.zoneIds,
                            workerId: requestedWorkerId,
                            // Match the currently-loaded calendar window so the calendar grid keeps the
                            // same range when switching workers (pagination state is preserved separately).
                            limitDays: values.calendarLoadedUntilDay ?? computeCalendarLimitDays(new Date(), new Date()),
                        },
                        values.packageDetailsV2 ?? null
                    )

                    if (workerCalendarRequestRef.current !== requestId) return

                    if (data?.packageInfo?.[0]) {
                        // V2 calendar returns a single calendar scoped to the selected worker; use preSelectSP.dayAvailable as workerDayAvailable.
                        const workerDayAvailable = data.packageInfo[0].preSelectSP?.dayAvailable || []
                        const routeOptimizationCfg =
                            pickRouteOptimizationCfg(calendar?.preSelectProvider) ?? null
                        const visitReminder = calendar?.preSelectProvider?.visitReminder ?? ''

                        setValues(values => {
                            if (values.worker !== requestedWorkerId) return values

                            return {
                                ...values,
                                workerDayAvailable: workerDayAvailable,
                                workerDayAvailableWorkerId: requestedWorkerId,
                                selectedWorkerRouteOptimizationCfg: routeOptimizationCfg,
                                selectedWorkerVisitReminder: visitReminder || undefined,
                                hasClusterRouteOptimization:
                                    calendar?.hasClusterRouteOptimization ??
                                    values.hasClusterRouteOptimization,
                                worker_available: values.worker_available.map((w) =>
                                    'workerId' in w && w.workerId === requestedWorkerId
                                        ? {
                                            ...w,
                                            routeOptimizationCfg: routeOptimizationCfg ?? undefined,
                                            visitReminder: visitReminder || undefined,
                                        }
                                        : w
                                ),
                                date_time: undefined,
                                loadingType: undefined,
                            }
                        })
                        // Also reset field touched to clear any validation errors
                        setFieldTouched('date_time', false)
                    } else {
                        setValues(values =>
                            values.worker === requestedWorkerId
                                ? {
                                    ...values,
                                    loadingType: undefined,
                                    workerDayAvailable: [],
                                    workerDayAvailableWorkerId: requestedWorkerId,
                                }
                                : values
                        )
                    }
                } catch (error) {
                    if (workerCalendarRequestRef.current !== requestId) return
                    console.error('Error fetching package available:', error)
                    toast.error(`${t('ERROR_MESSAGE.PACKAGE_ERROR.UNEXPECTED_ERROR')}`, ToastDefaultConfig())
                    setValues(values =>
                        values.worker === requestedWorkerId
                            ? {
                                ...values,
                                loadingType: undefined,
                                workerDayAvailable: [],
                                workerDayAvailableWorkerId: requestedWorkerId,
                            }
                            : values
                    )
                }
            }

            fetchPackageAvailable()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isClusterLogic,
        flow_type,
        values.worker,
        values.main_service?.data?.packageWorkFlowId,
        values.main_service?.id,
        values.preSelectSP?.companyId,
        values.preSelectSP?.spId,
        values.package,
        values.location?.countryId,
        values.customer_id,
        values.favWorkerIds,
        values.spIds,
        values.zoneIds,
        t
    ])

    // Unselect worker + "Based on Availability" when package changes.
    const prevPackageRef = useRef<number | undefined>(undefined)
    useEffect(() => {
        if (flow_type !== FLOW_TYPE.PACKAGE || !values.package) return

        if (prevPackageRef.current !== undefined && prevPackageRef.current !== values.package) {
            setState(prev => ({ ...prev, workersListLoading: true }))
            setValues((v) => ({
                ...v,
                loadingType: 'worker',
                preSelectSP: undefined,
                preSelectSPBaseDayAvailable: undefined,
                worker_available: [],
                worker: undefined,
                workerDayAvailable: undefined,
                workerDayAvailableWorkerId: undefined,
                selectedWorkerRouteOptimizationCfg: undefined,
                selectedWorkerVisitReminder: undefined,
                selectedDateRouteOptimizationCfg: undefined,
                selectedDateVisitReminder: undefined,
                hasClusterRouteOptimization: false,
                date_time: undefined,
            }))
            setFieldTouched('worker', false)
            setFieldTouched('date_time', false)
        }
        prevPackageRef.current = values.package
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [flow_type, values.package])

    // When switching back to DEFAULT_WORKER ("Based on Availability"), clear worker-specific availability
    // so calendar always uses preSelectSPBaseDayAvailable/preSelectSP.dayAvailable.
    useEffect(() => {
        if (
            flow_type === FLOW_TYPE.PACKAGE &&
            values.worker === DEFAULT_WORKER &&
            (values.workerDayAvailable || values.selectedWorkerRouteOptimizationCfg)
        ) {
            setValues(values => ({
                ...values,
                workerDayAvailable: undefined,
                workerDayAvailableWorkerId: undefined,
                selectedWorkerRouteOptimizationCfg: undefined,
                selectedWorkerVisitReminder: undefined,
                selectedDateRouteOptimizationCfg: undefined,
                selectedDateVisitReminder: undefined,
                date_time: undefined,
            }))
            setFieldTouched('date_time', false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [flow_type, values.worker])

    const checkNotFoundSearch = () => {
        if (!state.textSearch) return false;
        if (values.worker_available?.length) {
            let index = values.worker_available.findIndex(e => {
                let name = 'fullNameEn' in e ? (language === Languages.ar ? e.fullNameAr : e.fullNameEn) : (language === Languages.ar ? e.workerNameAr : e.workerNameEn);
                return name.toLowerCase().indexOf(state.textSearch.toLowerCase()) >= 0;
            })
            if (index != -1) return false;
        }
        return true;
    }

    const isWorkerCalendarLoading =
        isClusterLogic &&
        flow_type === FLOW_TYPE.PACKAGE &&
        values.worker &&
        values.worker !== DEFAULT_WORKER &&
        values.workerDayAvailableWorkerId !== values.worker

    const isWorkerListLoading =
        state.workersListLoading ||
        values.loadingType === 'worker' ||
        isWorkerCalendarLoading

    return (
        <AccordionItem id={'worker'} title={trans.title}>
            <div className='worker-section'>
                <p className='section-title d-flex align-items-center gap-2'>
                    <span>{trans.section_title}</span>
                </p>
                {!isWorkerListLoading && displayedWorkers.length > 0 && (
                    <>
                            <TextInput
                                placeholder={trans.search_placeholder}
                                inputGroup={<KTSVG defaultColor={true} path='/assets/images/icons/search-small.svg' />}
                                onChange={(e) => setState({ ...state, textSearch: e.target.value || '' })}
                            />
                            {checkNotFoundSearch() && <div className="invalid-message">{t("CREATE_ORDER_DRAWER.SECTION_4.SEARCH_SP_NOT_FOUND")}</div>}
                            <FieldRadioGroup
                                key={`${values.package ?? 'no-package'}-${values.worker ?? 'unselected'}`}
                                name={'worker'}
                                options={displayedWorkers || []}
                                optionKey={(e) => !('workerId' in e) ? e.spId : e.workerId}
                                renderRadio={({ Radio, onChange }) => (
                                    <div className='worker-list mt-5'>
                                        {Object.entries(Radio)
                                            .map(([, radio]) => ({ ...radio, label: !('workerId' in radio.item) ? (language === Languages.ar ? radio.item.fullNameAr : radio.item.fullNameEn) : (language === Languages.ar ? radio.item.workerNameAr : radio.item.workerNameEn) }))
                                            .filter(e => state.textSearch.trim() === '' || e.label?.toLowerCase().indexOf(state.textSearch.toLowerCase()) >= 0)
                                            .map((e, index) => (
                                                <div className='worker-item' key={index} onClick={() => onChange(e.value)}>
                                                    <img
                                                        className='avatar'
                                                        alt=''
                                                        src={e.item.personalImageUrl || user?.companyLogo}
                                                    />
                                                    <div className='info ms-2'>
                                                        <span className='name'>{e.label}</span>
                                                        {'workerId' in e.item && <span className='start-date'>{t('CREATE_ORDER_DRAWER.SECTION_4.START_DATE') + ': ' + moment(e.item.startDate || new Date()).locale(language === Languages.ar ? 'ar-SA' : 'en-US').format('DD/MM/YYYY')}</span>}
                                                        {!('workerId' in e.item) && !isOffer && <>
                                                            {e.item.isAvailableAll ?
                                                                <span className='fs-6 text-success'>{t('CREATE_ORDER_DRAWER.SECTION_4.DO_ALL')}</span>
                                                                : <>
                                                                    {e.item.servicesAvailable && <span className='fs-6 text-success'>
                                                                        {t('CREATE_ORDER_DRAWER.SECTION_4.CAN_DO')} {e.item.servicesAvailable}

                                                                    </span>
                                                                    }

                                                                    {e.item.servicesUnavailable && <span className='fs-6 text-danger'>
                                                                        {t('CREATE_ORDER_DRAWER.SECTION_4.CANT_DO')} {e.item.servicesUnavailable}
                                                                    </span>
                                                                    }
                                                                </>
                                                            }
                                                        </>}
                                                        {!('workerId' in e.item) && (e.item.isSpJoinOffer === false) && <div className='text-danger'>{t('REASSIGN_MODAL.ERROR_NOT_SUBSCRIBED')}</div>}
                                                    </div>

                                                    {'workerId' in e.item && !e.item.isAuto && (
                                                        <div
                                                            className='favorite'
                                                            onClick={(event) => {
                                                                event.stopPropagation()
                                                                handleFavoriteWorker(e.item)
                                                            }}
                                                        >
                                                            <div className='fav-icon-wrap'>
                                                                <span className={`fav-icon ${values.favWorkerIds.includes(e.item.workerId) && 'favorited'}`}></span>
                                                            </div>
                                                            <span>{t('CREATE_ORDER_DRAWER.SECTION_4.FAVORITE_WORKER')}</span>
                                                        </div>
                                                    )}
                                                    <div className='radio-check'>{e.component}</div>
                                                </div>
                                            ))}
                                    </div>
                                )}
                            />
                    </>
                )}
            </div>
        </AccordionItem >
    )
}

export default WorkerComponent
